/* Dataset → Summary: aggregate any selection per month / quarter / year.
 *
 * Stocks are never summed — the totals row is blank for a stock column and the
 * statistic selector only appears when a stock is in the selection.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API, SeriesTree, Sparkline, PALETTE, fmtNum, natureMeta, seriesLabel, yearOf } from './datasetCommon';
import exportDatasetSummaryPdf from './datasetSummaryPdf';

const PERIODS = [
  { key: 'month',   label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year',    label: 'Year' },
];

const STATS = [
  { key: 'mean', label: 'Mean' },
  { key: 'min',  label: 'Min' },
  { key: 'max',  label: 'Max' },
  { key: 'end',  label: 'End' },
];

const ELECTRICITY = /electric|strom|power/i;

/** Preset selections, resolved against the live catalogue. */
export function presetSeries(series, preset) {
  switch (preset) {
    case 'production':
      return series.filter((s) => s.nature === 'production');
    case 'generation':
      return series.filter((s) => s.nature === 'generation');
    case 'electricity':
      return series.filter((s) => (
        s.nature === 'generation'
        || s.nature === 'consumption_el'
        || (s.nature === 'exchange' && ELECTRICITY.test(s.resource || ''))
        || s.nature === 'battery_flow'
      ));
    case 'storage':
      return series.filter((s) => s.nature === 'storage_soc');
    default:
      return [];
  }
}

const PRESETS = [
  { key: 'production',  label: 'Production' },
  { key: 'generation',  label: 'Generation' },
  { key: 'electricity', label: 'Electricity balance' },
  { key: 'storage',     label: 'Storage' },
];

const DatasetSummaryTab = ({ project, dataset, series }) => {
  const firstYear = yearOf(dataset && dataset.ts_start, dataset && dataset.tz) || 2028;
  const lastYear = yearOf(dataset && dataset.ts_end, dataset && dataset.tz) || firstYear;

  const [period, setPeriod] = useState('year');
  const [yearFrom, setYearFrom] = useState(firstYear);
  const [yearTo, setYearTo] = useState(lastYear);
  const [selected, setSelected] = useState([]);
  const [stat, setStat] = useState('mean');
  const [massUnit, setMassUnit] = useState('t');
  const [energyUnit, setEnergyUnit] = useState('MWh');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportMsg, setExportMsg] = useState(null);

  const datasetId = dataset && dataset.id;

  // Year pickers follow the dataset when it changes; Month defaults to one year.
  useEffect(() => {
    setYearFrom(period === 'month' ? lastYear : firstYear);
    setYearTo(lastYear);
  }, [period, firstYear, lastYear]);

  const byId = useMemo(() => {
    const m = new Map();
    series.forEach((s) => m.set(s.id, s));
    return m;
  }, [series]);

  const hasStock = useMemo(() => selected.some((id) => byId.get(id) && byId.get(id).is_stock), [selected, byId]);
  const hasSelection = selected.length > 0;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set('period', period);
    p.set('year_from', String(yearFrom));
    p.set('year_to', String(yearTo));
    p.set('series', selected.join(','));
    p.set('stat', stat);
    p.set('mass_unit', massUnit);
    p.set('energy_unit', energyUnit);
    return p.toString();
  }, [period, yearFrom, yearTo, selected, stat, massUnit, energyUnit]);

  useEffect(() => {
    if (!datasetId || !hasSelection) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/result-datasets/${datasetId}/summary?${query}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) { setData(body); setError(null); }
      } catch (err) {
        if (!cancelled) { setData(null); setError(err.message || String(err)); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [datasetId, query, hasSelection]);

  const applyPreset = useCallback((key) => {
    setSelected(presetSeries(series, key).map((s) => s.id));
  }, [series]);

  const exportExcel = () => {
    if (!datasetId || !hasSelection) return;
    const a = document.createElement('a');
    a.href = `${API}/api/result-datasets/${datasetId}/summary.xlsx?${query}`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportPdf = async () => {
    if (!data) return;
    setExportMsg('Building PDF…');
    try {
      const out = await exportDatasetSummaryPdf({
        projectCode: project.project_code,
        projectName: project.name,
        dataset,
        summary: data,
        query: { period, yearFrom, yearTo, stat, massUnit, energyUnit },
      });
      setExportMsg(out.cancelled ? 'Export cancelled.' : `Saved ${out.filename}`);
    } catch (err) {
      setExportMsg(`Export failed: ${err.message || String(err)}`);
    }
    setTimeout(() => setExportMsg(null), 6000);
  };

  const years = [];
  for (let y = firstYear; y <= lastYear; y++) years.push(y);

  const columns = (data && data.columns) || [];
  const rows = (data && data.rows) || [];
  const totals = (data && data.totals) || [];

  return (
    <div className="ds-split">
      {/* ---------------- left rail ---------------- */}
      <div className="ds-rail">
        <div className="ds-rail-block">
          <div className="ds-sub">Period</div>
          <div className="climate-toolbar-tabs">
            {PERIODS.map((p) => (
              <button key={p.key}
                      className={`toolbar-tab ${period === p.key ? 'active' : ''}`}
                      onClick={() => setPeriod(p.key)}>{p.label}</button>
            ))}
          </div>
        </div>

        <div className="ds-rail-block">
          <div className="ds-sub">Range</div>
          <div className="ds-row-2">
            <label className="ds-field">
              <span>From</span>
              <select className="ds-input ds-input-sm" value={yearFrom}
                      onChange={(e) => setYearFrom(Number(e.target.value))}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="ds-field">
              <span>To</span>
              <select className="ds-input ds-input-sm" value={yearTo}
                      onChange={(e) => setYearTo(Number(e.target.value))}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="ds-rail-block">
          <div className="ds-sub">Presets</div>
          <div className="ds-chip-row">
            {PRESETS.map((p) => (
              <button key={p.key} className="ds-chip ds-chip-btn"
                      style={{ borderColor: '#334155', color: '#cbd5e1' }}
                      onClick={() => applyPreset(p.key)}>{p.label}</button>
            ))}
            <button className="ds-chip ds-chip-btn" style={{ borderColor: '#334155', color: '#cbd5e1' }}
                    onClick={() => setSelected([])}>Clear</button>
          </div>
        </div>

        <div className="ds-rail-block ds-rail-grow">
          <div className="ds-sub">Series <span className="ds-muted">({selected.length} selected)</span></div>
          <SeriesTree series={series} selected={selected} onChange={setSelected} maxHeight={340} />
        </div>

        {hasStock && (
          <div className="ds-rail-block">
            <div className="ds-sub">Stock statistic</div>
            <div className="climate-toolbar-tabs">
              {STATS.map((s) => (
                <button key={s.key}
                        className={`toolbar-tab ${stat === s.key ? 'active' : ''}`}
                        onClick={() => setStat(s.key)}>{s.label}</button>
              ))}
            </div>
          </div>
        )}

        <div className="ds-rail-block">
          <div className="ds-sub">Units</div>
          <div className="climate-toolbar-tabs" style={{ marginBottom: 6 }}>
            {['kg', 't'].map((u) => (
              <button key={u} className={`toolbar-tab ${massUnit === u ? 'active' : ''}`}
                      onClick={() => setMassUnit(u)}>{u}</button>
            ))}
          </div>
          <div className="climate-toolbar-tabs">
            {['MWh', 'GWh'].map((u) => (
              <button key={u} className={`toolbar-tab ${energyUnit === u ? 'active' : ''}`}
                      onClick={() => setEnergyUnit(u)}>{u}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- main panel ---------------- */}
      <div className="ds-main">
        <div className="ds-panel-head" style={{ padding: '0 0 10px' }}>
          <h3>Summary</h3>
          <span className="ds-muted">
            {loading ? 'loading…' : `${rows.length} period${rows.length === 1 ? '' : 's'} × ${columns.length} series`}
            {hasStock ? ` · stock statistic: ${stat}` : ''}
          </span>
          <div style={{ flex: 1 }} />
          {exportMsg && <span className="ds-muted">{exportMsg}</span>}
          <button className="btn-ghost" onClick={exportExcel} disabled={!hasSelection}>Export Excel</button>
          <button className="btn-accent" onClick={exportPdf} disabled={!data}>Export PDF</button>
        </div>

        {error && <div className="ds-error">{error}</div>}

        {!hasSelection && <div className="ds-empty">Pick series on the left, or start from a preset.</div>}

        {hasSelection && data && (
          <div className="ds-table-scroll ds-table-tall">
            <table className="ds-table ds-table-num">
              <thead>
                <tr className="ds-spark-row">
                  <th />
                  {columns.map((c, i) => (
                    <th key={`sp-${c.id}`}>
                      <Sparkline
                        values={rows.map((r) => r.values[i])}
                        color={PALETTE[i % PALETTE.length]}
                      />
                    </th>
                  ))}
                </tr>
                <tr>
                  <th>Period</th>
                  {columns.map((c) => (
                    <th key={c.id} title={`${natureMeta(c.nature).label}${c.is_stock ? ' · stock' : ''}`}>
                      <div className="ds-col-name">{c.display_name}</div>
                      <div className="ds-col-unit">
                        {c.unit || '—'}{c.is_stock ? ' · stock' : ''}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.period}>
                    <td className="ds-period">{r.period}</td>
                    {r.values.map((v, i) => (
                      <td key={`${r.period}-${columns[i] ? columns[i].id : i}`}>{fmtNum(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="ds-period">Total</td>
                  {columns.map((c, i) => (
                    <td key={`tot-${c.id}`}>{totals[i] === null || totals[i] === undefined ? '' : fmtNum(totals[i])}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {hasSelection && !data && !loading && !error && (
          <div className="ds-empty">No rows for this query.</div>
        )}

        {columns.length > 0 && (
          <div className="ds-legend">
            {columns.map((c, i) => (
              <span key={`lg-${c.id}`} className="ds-legend-item">
                <i style={{ background: PALETTE[i % PALETTE.length] }} />
                {c.display_name || seriesLabel(byId.get(c.id) || { id: c.id })}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DatasetSummaryTab;
